import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Stamp, Calendar, User, XCircle, Clock } from "lucide-react";
import { format, addDays } from "date-fns";
import { de } from "date-fns/locale";

interface CommitmentBlockProps {
  data?: {
    recommended_option_id?: string;
    recommended_option_label?: string;
    validity_days?: number;
    owner?: string;
    review_date?: string;
    committed_at?: string;
    excluded_options?: Array<{
      label: string;
      reason?: string;
      consequence?: string;
    }>;
  };
  recommendedOption?: string;
  options?: Array<{
    option_id: string;
    label: string;
  }>;
}

export function CommitmentBlock({ data, recommendedOption, options }: CommitmentBlockProps) {
  // If no explicit commitment data, show pending state
  const hasCommitment = data?.validity_days || data?.committed_at;
  
  const optionLabel = data?.recommended_option_label || recommendedOption || "Gewählte Option";
  const validityDays = data?.validity_days || 30;
  const owner = data?.owner;
  const reviewDate = data?.review_date 
    ? new Date(data.review_date) 
    : addDays(new Date(), validityDays);
  
  // Build excluded options from non-recommended options
  const excludedOptions = data?.excluded_options || 
    options?.filter(opt => opt.option_id !== data?.recommended_option_id)
      .map(opt => ({ label: opt.label, reason: "Nicht als Fokus-Option gewählt" })) || [];

  return (
    <Card id="commitment" className="scroll-mt-24 border-2 border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Stamp className="h-5 w-5 text-green-600" />
            Verbindlicher Commit
          </CardTitle>
          {hasCommitment ? (
            <Badge className="bg-green-500/20 text-green-700 border-green-500/30">
              Aktiv
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-500/30">
              Ausstehend
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Commitment Statement - User Input */}
        <div className="user-input bg-green-500/10 rounded-lg p-4 border border-green-500/20">
          <p className="text-base font-semibold text-green-800 dark:text-green-300">
            Diese Entscheidung gilt für {validityDays} Tage und ersetzt alle Alternativen.
          </p>
          <p className="text-sm text-green-700 dark:text-green-400 mt-2">
            Gewählte Option: <strong>{optionLabel}</strong>
          </p>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Validity */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Clock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Gültigkeitsdauer</p>
              <p className="text-sm font-semibold">{validityDays} Tage</p>
            </div>
          </div>

          {/* Review Date */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Review-Datum</p>
              <p className="text-sm font-semibold">
                {format(reviewDate, "d. MMMM yyyy", { locale: de })}
              </p>
            </div>
          </div>

          {/* Owner */}
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <User className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">Decision Owner</p>
              <p className="text-sm font-semibold">{owner || "Nicht zugewiesen"}</p>
            </div>
          </div>
        </div>

        {/* Excluded Options */}
        {excludedOptions.length > 0 && (
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-muted-foreground">Bewusste Ausschlüsse</h4>
            </div>
            <div className="space-y-2">
              {excludedOptions.map((opt, index) => {
                const displayReason = opt.consequence || opt.reason;
                return (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-red-500 shrink-0">✕</span>
                    <div>
                      <span className="font-medium">{opt.label}</span>
                      {displayReason && (
                        <span className="text-muted-foreground"> – {displayReason}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer Note */}
        <p className="text-xs text-muted-foreground text-center pt-3 border-t">
          Änderungen an dieser Entscheidung erfordern eine neue Bewertung in Phase 3.
        </p>
      </CardContent>
    </Card>
  );
}
